import { useMemo } from 'react';
import { ApiResponse } from 'apisauce';

import useImageHeaders from '@hooks/imageHooks/useImageHeaders';
import { ImageType } from '@models/Image';

export type UseImageDataAttr = {
  /**
   * Optional default image source if the main image is not found
   */
  defaultSrc?: string;
  /**
   * If true, disables the image fetching
   */
  disabled?: boolean;
  /**
   * If true, indicates that a delete handler is expected for context images.
   * Images that are not deletable when a handler is expected are treated as fallbacks.
   */
  expectsDeleteHandler?: boolean;
  /**
   * Optional handler to delete the image (if supported by the API)
   */
  handleDeleteImage?: () => Promise<ApiResponse<unknown>>;
  /**
   * Image identifier used for query caching
   */
  identifier: string;
  /**
   * Type of image (used for caching and image fetching)
   */
  imageType: ImageType;
  /**
   * Source URL of the image to fetch
   */
  src?: string;
};

/**
 * Custom hook to manage image fetching and attributes
 * @returns Image attributes and handlers
 */
const useImageData = ({
  defaultSrc,
  disabled,
  expectsDeleteHandler,
  identifier,
  imageType,
  src,
}: UseImageDataAttr) => {
  const imageMetadata = useImageHeaders({
    disableCache: true,
    disabled,
    identifier,
    imageType,
    src,
  });

  // An image is a fallback if:
  // 1. It's marked as a fallback by the API, OR
  // 2. It's not deletable but we expect it to be (meaning it's a default image returned instead of a context image)
  const isFallback =
    imageMetadata.isFallback || (expectsDeleteHandler && !imageMetadata.isDeletable);

  const contextImageMetadata = useMemo(
    () => (isFallback ? undefined : imageMetadata),
    [isFallback, imageMetadata]
  );
  const fallbackImageMetadata = useMemo(
    () => (isFallback ? imageMetadata : undefined),
    [isFallback, imageMetadata]
  );

  const defaultImageMetadata = useImageHeaders({
    disabled,
    identifier,
    imageType,
    src: defaultSrc,
  });

  const effectiveImageMetadata = useMemo(() => {
    if (contextImageMetadata?.isLoading) return;
    if (contextImageMetadata?.wasFound) return contextImageMetadata;
    if (defaultImageMetadata?.wasFound) return defaultImageMetadata;
    if (!fallbackImageMetadata?.isLoading) return fallbackImageMetadata;
  }, [contextImageMetadata, defaultImageMetadata, fallbackImageMetadata]);

  const isLoading = imageMetadata.isLoading || defaultImageMetadata.isLoading;

  const { refetch } = imageMetadata;

  return {
    contextImageMetadata,
    defaultImageMetadata,
    effectiveImageMetadata,
    fallbackImageMetadata,
    isLoading,
    refetch,
  };
};

export default useImageData;
